// src/screens/IngestMonitor.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getIngestSummary,
  getIngestTimeseries,
  getIngestGeoHeatmap,
  getIngestDrones,
} from "../api/ingestMonitor";
import { openFleetSSE } from "../api/sse";

import { THEME, appViewportStyle, btnStyle, screenStyle, panelStyle } from "../ui/theme";
import { Section, Segment, Card, Pill, EmptyState, KV } from "../ui/primitives";

import IngestActivityChart from "../layout/components/ingest/IngestActivityChart";
import IngestGeoHeatmap from "../layout/components/ingest/IngestGeoHeatmap";

/* =========================
   Timezone + NOC helpers
========================= */

const BR_TZ = "America/Sao_Paulo";

function fmtBRDateTime(x) {
  if (!x) return "—";
  const d = new Date(x);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR", { timeZone: BR_TZ });
}

function fmtBRTime(x) {
  if (!x) return "—";
  const d = new Date(x);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("pt-BR", { timeZone: BR_TZ, hour: "2-digit", minute: "2-digit" });
}

function silenceLevel(seconds) {
  if (seconds == null) return { label: "SEM DADOS", tone: "muted" };
  if (seconds < 5 * 60) return { label: "OK", tone: "ok" };
  if (seconds < 15 * 60) return { label: "WARN", tone: "warn" };
  return { label: "ALERT", tone: "alert" };
}

function fmtAgo(seconds) {
  if (seconds == null) return "—";
  const s = Math.max(0, Number(seconds));
  if (!Number.isFinite(s)) return "—";
  if (s < 60) return `${Math.round(s)}s`;
  const m = s / 60;
  if (m < 60) return `${m.toFixed(1)}m`;
  const h = m / 60;
  return `${h.toFixed(1)}h`;
}

function toNumOrNull(x) {
  if (x == null) return null;
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

function toNumOrZero(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}

/* =========================
   Helpers: formatting
========================= */

function fmtHrsFromMs(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n)) return "—";
  const h = n / 3600000;
  if (h < 1) return `${Math.round(n / 60000)} min`;
  return `${h.toFixed(1)} h`;
}

function fmtArea(x) {
  if (x == null) return "—";
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("pt-BR", { maximumFractionDigits: 1 });
}

/**
 * Janela canônica local (BR) para UI (labels).
 * offset: 0 corrente, -1 anterior
 */
function getCanonicalWindowLabel(range, offset = 0) {
  const now = new Date();

  const ymd = new Intl.DateTimeFormat("en-CA", {
    timeZone: BR_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);

  const [Y, M, D] = ymd.split("-").map((x) => Number(x));

  const fmtShort = (dateObj) =>
    dateObj.toLocaleDateString("pt-BR", { timeZone: BR_TZ, day: "2-digit", month: "2-digit" });

  const fmtLong = (dateObj) =>
    dateObj.toLocaleDateString("pt-BR", {
      timeZone: BR_TZ,
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

  const utcNoon = (y, m0, d0) => new Date(Date.UTC(y, m0, d0, 12, 0, 0));

  if (range === "day") {
    const dayObj = offset === -1 ? utcNoon(Y, M - 1, D - 1) : utcNoon(Y, M - 1, D);
    const tag = offset === -1 ? "Ontem" : "Hoje";
    return `${tag} (${fmtLong(dayObj)}) • 00:00–24:00 (BR)`;
  }

  if (range === "week") {
    const wd = new Intl.DateTimeFormat("pt-BR", { timeZone: BR_TZ, weekday: "short" })
      .format(now)
      .toLowerCase();

    const map = {
      dom: 0,
      "dom.": 0,
      seg: 1,
      "seg.": 1,
      ter: 2,
      "ter.": 2,
      qua: 3,
      "qua.": 3,
      qui: 4,
      "qui.": 4,
      sex: 5,
      "sex.": 5,
      sáb: 6,
      "sáb.": 6,
      sab: 6,
      "sab.": 6,
    };

    const dowBR = map[wd] ?? 0;

    let sunday = utcNoon(Y, M - 1, D - dowBR);
    let saturday = utcNoon(Y, M - 1, D - dowBR + 6);

    if (offset === -1) {
      sunday = utcNoon(Y, M - 1, D - dowBR - 7);
      saturday = utcNoon(Y, M - 1, D - dowBR - 1);
    }

    const tag = offset === -1 ? "Semana anterior" : "Semana";
    return `${tag} (Dom–Sáb) • ${fmtShort(sunday)} → ${fmtShort(saturday)} (BR)`;
  }

  if (range === "year") {
    const year = offset === -1 ? Y - 1 : Y;
    return `${offset === -1 ? "Ano anterior" : "Ano"} • ${year} (BR)`;
  }

  const firstThis = utcNoon(Y, M - 1, 1);
  const first = offset === -1 ? utcNoon(Y, M - 2, 1) : firstThis;

  const monthName = first.toLocaleDateString("pt-BR", {
    timeZone: BR_TZ,
    month: "long",
    year: "numeric",
  });

  const tag = offset === -1 ? "Mês anterior" : "Mês";
  return `${tag} • ${monthName} (BR)`;
}

/**
 * ✅ Janela canônica em MS (UTC epoch) — usada como domínio do eixo X.
 * BR fixo UTC-03:00 => 00:00 BR = 03:00 UTC.
 */
function getCanonicalWindowMs(range, offset = 0) {
  const now = new Date();

  const ymd = new Intl.DateTimeFormat("en-CA", {
    timeZone: BR_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);

  const [Y, M, D] = ymd.split("-").map(Number);

  const brMidnightToUtcMs = (y, m0, d0) => Date.UTC(y, m0, d0, 3, 0, 0, 0);

  if (range === "day") {
    const day = offset === -1 ? D - 1 : D;
    const start = brMidnightToUtcMs(Y, M - 1, day);
    const end = start + 24 * 60 * 60 * 1000;
    return { start, end };
  }

  if (range === "week") {
    const wd = new Intl.DateTimeFormat("pt-BR", { timeZone: BR_TZ, weekday: "short" })
      .format(now)
      .toLowerCase();

    const map = {
      dom: 0,
      "dom.": 0,
      seg: 1,
      "seg.": 1,
      ter: 2,
      "ter.": 2,
      qua: 3,
      "qua.": 3,
      qui: 4,
      "qui.": 4,
      sex: 5,
      "sex.": 5,
      sáb: 6,
      "sáb.": 6,
      sab: 6,
      "sab.": 6,
    };

    const dow = map[wd] ?? 0;
    const baseSundayDay = offset === -1 ? D - dow - 7 : D - dow;

    const start = brMidnightToUtcMs(Y, M - 1, baseSundayDay);
    const end = start + 7 * 24 * 60 * 60 * 1000;
    return { start, end };
  }

  if (range === "month") {
    const y = Y;
    const m0 = offset === -1 ? M - 2 : M - 1;
    const start = brMidnightToUtcMs(y, m0, 1);
    const next = new Date(Date.UTC(y, m0 + 1, 1, 3, 0, 0, 0)).getTime();
    return { start, end: next };
  }

  const year = offset === -1 ? Y - 1 : Y;
  const start = brMidnightToUtcMs(year, 0, 1);
  const end = brMidnightToUtcMs(year + 1, 0, 1);
  return { start, end };
}

/* =========================
   Local: Drone list (mobile)
========================= */

function DroneList({ items, isLoading, isError }) {
  if (isLoading) return <div style={{ color: THEME.muted, padding: 12 }}>Carregando drones…</div>;
  if (isError)
    return (
      <div style={{ color: "rgba(255,120,120,0.9)", padding: 12 }}>
        Falha ao carregar drones
      </div>
    );

  const rows = (items || []).slice(0, 80);
  if (!rows.length) return <EmptyState>Sem drones nessa janela.</EmptyState>;

  return (
    <div style={{ display: "grid", gap: 10 }}>
      {rows.map((d) => {
        const flights = Number(d?.flights || 0);
        const activeDays = Number(d?.active_days || 0);
        const area = d?.work_area_sum;
        const dur = d?.duration_ms_sum;
        const work = d?.work_time_ms_sum;

        return (
          <div key={d.hardware_id} style={panelStyle({ padding: 12 })}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 10,
                alignItems: "center",
              }}
            >
              <div style={{ fontWeight: 950, fontSize: 13 }}>{d.hardware_id || "—"}</div>

              <span
                style={{
                  padding: "4px 10px",
                  borderRadius: THEME.radiusPill,
                  border: `1px solid ${THEME.border}`,
                  background: "rgba(255,255,255,0.06)",
                  fontSize: 12,
                  color: THEME.muted,
                  fontWeight: 900,
                  whiteSpace: "nowrap",
                }}
              >
                {flights} voos
                {activeDays ? ` • ${activeDays} dias` : ""}
              </span>
            </div>

            <div
              style={{
                marginTop: 10,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
              }}
            >
              <KV label="Último" value={fmtBRDateTime(d.last_event_at)} />
              <KV label="Primeiro" value={fmtBRDateTime(d.first_event_at)} />

              <KV label="Duração (Σ)" value={fmtHrsFromMs(dur)} />
              <KV label="Work time (Σ)" value={fmtHrsFromMs(work)} />

              <KV label="Área (Σ)" value={fmtArea(area)} />
              <KV
                label="Lag (created→end)"
                value={
                  d?.lag_created_end_min_avg != null
                    ? `${Number(d.lag_created_end_min_avg).toFixed(1)}m`
                    : "—"
                }
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function IngestMonitor() {
  const queryClient = useQueryClient();

  const [mode, setMode] = useState("created");
  const [range, setRange] = useState("month");
  const [offset, setOffset] = useState(0);

  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [live, setLive] = useState(false);

  const [err, setErr] = useState(null);
  const refreshTimer = useRef(null);

  const [nowTick, setNowTick] = useState(Date.now());
  const silenceBaseRef = useRef({ baseSec: null, baseAt: null });

  // heatmap query (react-query)
  const grid_m = range === "day" ? 150 : range === "week" ? 250 : range === "month" ? 500 : 700;

  const geoQuery = useQuery({
    queryKey: ["ingest", "geoHeatmap", { mode, range, offset, grid_m }],
    queryFn: () => getIngestGeoHeatmap({ mode, range, offset, grid_m, limit: 20000 }),
    staleTime: 30_000,
    keepPreviousData: true,
  });

  const points = geoQuery.data?.points ?? [];

  // ✅ drones query (react-query)
  const dronesQuery = useQuery({
    queryKey: ["ingest", "drones", { mode, range, offset }],
    queryFn: () => getIngestDrones({ mode, range, offset, limit: 80 }),
    staleTime: 30_000,
    keepPreviousData: true,
  });

  useEffect(() => {
    setOffset(0);
  }, [range]);

  const eventsLabel = useMemo(() => {
    if (range === "day") return offset === -1 ? "Vôos (ontem)" : "Vôos (hoje)";
    if (range === "week") return offset === -1 ? "Vôos (semana anterior)" : "Vôos (semana)";
    if (range === "year") return offset === -1 ? "Vôos (ano anterior)" : "Vôos (ano)";
    return offset === -1 ? "Vôos (mês anterior)" : "Vôos (mês)";
  }, [range, offset]);

  const windowLabel = useMemo(() => getCanonicalWindowLabel(range, offset), [range, offset]);
  const windowMs = useMemo(() => getCanonicalWindowMs(range, offset), [range, offset]);

  // ✅ inclui flights_mm30 / drones_mm30
  const normalizeSeries = useCallback((items) => {
    const arr = Array.isArray(items) ? items : [];
    return arr.map((r) => ({
      bucket: r?.bucket ?? "",
      bucket_ms: toNumOrNull(r?.bucket_start_utc_ms),
      bucket_start_utc_ms: toNumOrNull(r?.bucket_start_utc_ms),

      flights: toNumOrZero(r?.flights),
      drones: toNumOrZero(r?.drones),

      flights_mm30: toNumOrNull(r?.flights_mm30),
      drones_mm30: toNumOrNull(r?.drones_mm30),

      lag_min_avg: toNumOrNull(r?.lag_min_avg),
      lag_created_end_min_avg: toNumOrNull(r?.lag_created_end_min_avg),
    }));
  }, []);

  const loadAll = useCallback(async () => {
    setErr(null);
    try {
      const [s1, s2] = await Promise.all([
        getIngestSummary(mode, range, offset),
        getIngestTimeseries(mode, range, offset),
      ]);

      setSummary(s1);
      setSeries(normalizeSeries(s2?.items));
      setErr(null);
    } catch (e) {
      setErr(e?.message || String(e));
    }
  }, [mode, range, offset, normalizeSeries]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const scheduleRefresh = useCallback(() => {
    if (refreshTimer.current) return;
    refreshTimer.current = setTimeout(async () => {
      refreshTimer.current = null;
      try {
        const [s1, s2] = await Promise.all([
          getIngestSummary(mode, range, offset),
          getIngestTimeseries(mode, range, offset),
        ]);
        setSummary(s1);
        setSeries(normalizeSeries(s2?.items));
        setErr(null);
      } catch (e) {
        setErr(e?.message || String(e));
      }
    }, 1200);
  }, [mode, range, offset, normalizeSeries]);

  useEffect(() => {
    const es = openFleetSSE();

    es.addEventListener("hello", () => setLive(true));

    es.addEventListener("flight.new", () => {
      setLive(true);
      if (offset === -1) return;

      // ✅ React Query: invalida tudo do "ingest" (summary/timeseries/geo/drones)
      queryClient.invalidateQueries({ queryKey: ["ingest"] });

      // ✅ mantém seu refresh atual (fallback / e para manter o summary/series em state)
      scheduleRefresh();
    });

    es.onerror = () => setLive(false);
    return () => es.close();
  }, [scheduleRefresh, offset, queryClient]);

  useEffect(() => {
    const s = summary?.seconds_since_last;
    if (s == null) {
      silenceBaseRef.current = { baseSec: null, baseAt: null };
      return;
    }
    const n = Number(s);
    if (!Number.isFinite(n)) {
      silenceBaseRef.current = { baseSec: null, baseAt: null };
      return;
    }
    silenceBaseRef.current = { baseSec: n, baseAt: Date.now() };
  }, [summary?.seconds_since_last]);

  useEffect(() => {
    const isLiveWindow = offset === 0 && live;
    if (!isLiveWindow) return;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [offset, live]);

  const secondsSinceLastLive = useMemo(() => {
    const { baseSec, baseAt } = silenceBaseRef.current || {};
    if (baseSec == null || baseAt == null) return null;
    const delta = (nowTick - baseAt) / 1000;
    return Math.max(0, baseSec + delta);
  }, [nowTick]);

  const sl = useMemo(() => silenceLevel(secondsSinceLastLive), [secondsSinceLastLive]);

  const COLORS = useMemo(
    () => ({
      flightsBar: THEME.chartPrimary,
      flightsLine: THEME.chartPrimary,
      dronesLine: THEME.chartSecondary,
      dronesBar: THEME.chartSecondary,
    }),
    []
  );

  const hasAnyData = useMemo(() => {
    if (!Array.isArray(series) || !series.length) return false;
    return series.some((r) => (r.flights || 0) > 0 || (r.drones || 0) > 0);
  }, [series]);

  const statusTitle = useMemo(() => {
    if (offset === -1) return "◌ HISTÓRICO";
    if (live) return "● LIVE";
    return "○ OFFLINE";
  }, [offset, live]);

  const mmEnabled = range === "month" || range === "year";
  const mmLabel = mmEnabled ? "MM30" : "MM30 (apenas mês/ano)";

  return (
    <div style={appViewportStyle()}>
      <div style={screenStyle()}>
        <div style={{ maxWidth: 980, margin: "0 auto", display: "grid", gap: 12 }}>
          <Section
            title={
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <b style={{ fontSize: 14, letterSpacing: 0.4 }}>{statusTitle}</b>

                {offset === 0 && live && (
                  <Pill tone={sl.tone}>
                    {sl.label} • {fmtAgo(secondsSinceLastLive)}
                  </Pill>
                )}
              </div>
            }
            subtitle={windowLabel}
            right={
              <button onClick={loadAll} style={btnStyle(false)}>
                ↻ Recarregar
              </button>
            }
          >
            {err && (
              <div
                style={{
                  marginTop: 2,
                  padding: 10,
                  borderRadius: 14,
                  border: "1px solid rgba(251,113,133,0.28)",
                  background: "rgba(251,113,133,0.12)",
                  color: THEME.text,
                  fontSize: 12,
                }}
              >
                <b style={{ color: THEME.red }}>Erro:</b> {err}
              </div>
            )}
          </Section>

          <Section>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Segment
                label="Tempo"
                value={mode}
                options={[
                  ["ingested", "Ingestão"],
                  ["created", "Criação"],
                ]}
                onChange={setMode}
              />
              <Segment
                label="Janela"
                value={range}
                options={[
                  ["day", "Dia"],
                  ["week", "Semana"],
                  ["month", "Mês"],
                  ["year", "Ano"],
                ]}
                onChange={setRange}
              />
            </div>

            <div
              style={{
                marginTop: 10,
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: 12, color: THEME.muted, fontWeight: 900 }}>Período</span>
              <button onClick={() => setOffset(-1)} style={btnStyle(offset === -1)}>
                ◀ Anterior
              </button>
              <button onClick={() => setOffset(0)} style={btnStyle(offset === 0)}>
                Corrente
              </button>

              <span style={{ marginLeft: 8, fontSize: 12, color: THEME.muted, fontWeight: 800 }}>
                {mmLabel}
              </span>
            </div>
          </Section>

          <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
            <Card label={eventsLabel} value={summary?.flights ?? "—"} />
            <Card label="Drones em operação" value={summary?.drones ?? "—"} />
            <Card label="Último vôo" value={fmtBRTime(summary?.last_event_at)} />
            <Card label="Silêncio" value={fmtAgo(summary?.seconds_since_last)} />
            <Card
              label="Lag (created→ingest)"
              value={summary?.lag_min_avg != null ? `${Number(summary.lag_min_avg).toFixed(1)}m` : "—"}
            />
            <Card
              label="Lag (created→end)"
              value={
                summary?.lag_created_end_min_avg != null
                  ? `${Number(summary.lag_created_end_min_avg).toFixed(1)}m`
                  : "—"
              }
            />
          </div>

          <Section
            title="Atividade por bucket"
            subtitle={mode === "ingested" ? "baseado em ingested_at" : "baseado em created_at"}
          >
            {!hasAnyData && (
              <EmptyState style={{ marginBottom: 8 }}>
                Sem pontos nessa janela (ou a API retornou tudo zerado). Se você esperava dados aqui,
                vale checar se o backend está recebendo <code>offset</code>.
              </EmptyState>
            )}

            <div style={{ display: "grid", gap: 12 }}>
              {/* ✅ TOP: flights (bar) + drones_mm30 (line aux) */}
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
                  Voos <span style={{ color: THEME.muted, fontWeight: 700 }}>• linha: tendência</span>
                </div>
                <div style={{ height: 220 }}>
                  <IngestActivityChart
                    series={series}
                    seriesA={{ key: "flights", name: "Voos", variant: "bar", axis: "left" ,color: COLORS.dronesLine}}
                    seriesB={{
                      key: "flights_mm30",
                      name: "Tendência Vôos",
                      variant: "line",
                      axis: "right",
                      // ✅ força cor da MM30 para ficar igual “drones”
                      color: COLORS.flightsLine,
                    }}
                    metric="flights"
                    metricName="Voos"
                    offset={offset}
                    nowTick={nowTick}
                    range={range}
                    theme={THEME}
                    colors={COLORS}
                    windowStartMs={windowMs?.start}
                    windowEndMs={windowMs?.end}
                  />
                </div>
              </div>

              {/* ✅ BOTTOM: drones (bar) + drones_mm30 (line aux) */}
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
                  Drones <span style={{ color: THEME.muted, fontWeight: 700 }}>• linha: tendência</span>
                </div>
                <div style={{ height: 220 }}>
                  <IngestActivityChart
                    series={series}
                    seriesA={{ key: "drones", name: "Drones", variant: "bar", axis: "left" , fill: COLORS.dronesBar}}
                    seriesB={{
                      key: "drones_mm30",
                      name: "Tentência Drones",
                      variant: "line",
                      axis: "right",
                      color: COLORS.dronesLine,
                    }}
                    metric="drones"
                    metricName="Drones"
                    offset={offset}
                    nowTick={nowTick}
                    range={range}
                    theme={THEME}
                    colors={COLORS}
                    windowStartMs={windowMs?.start}
                    windowEndMs={windowMs?.end}
                  />
                </div>
              </div>
            </div>
          </Section>

          <Section
            title="Mapa de calor geográfico"
            subtitle={
              geoQuery.data
                ? `${geoQuery.data.count ?? 0} regiões • resolução ${geoQuery.data.grid_m ?? grid_m}m • ${
                    mode === "ingested" ? "ingested_at" : "created_at"
                  }`
                : "Distribuição espacial das operações no período selecionado"
            }
          >
            {geoQuery.isLoading ? (
              <div style={{ color: THEME.muted ?? "rgba(255,255,255,0.7)", padding: 12 }}>
                Carregando mapa…
              </div>
            ) : geoQuery.isError ? (
              <div style={{ color: "rgba(255,120,120,0.9)", padding: 12 }}>
                Falha ao carregar heatmap
              </div>
            ) : (
              <IngestGeoHeatmap points={points} theme={THEME} height={360} />
            )}
          </Section>

          <Section
            title="Drones no período"
            subtitle={
              dronesQuery.data
                ? `${dronesQuery.data.count ?? 0} drones • resumo por hardware_id`
                : "Resumo por drone na janela selecionada"
            }
  
          >
            <DroneList items={dronesQuery.data?.items} isLoading={dronesQuery.isLoading} isError={dronesQuery.isError} />
          </Section>
        </div>
      </div>
    </div>
  );
}
